import { useEffect, useState } from "react";
import { motion } from "framer-motion";

/**
 * Cinematic reveal wrapper.
 *
 * Originally used `clip-path: inset(100%)` for a theatrical unveil — but
 * Chrome Android has a documented bug where `inset(100%)` stays stuck at
 * "fully clipped" and `whileInView` never restores it, blanking the entire
 * BentoCard grid on the home page.
 *
 * Switched to a plain opacity + translate animation: visually similar,
 * cross-browser safe, no dependency on clip-path.
 *
 * Direction presets (translate axis):
 *   "up"    (default) – slides up from below
 *   "down"            – slides down from above
 *   "left"            – slides in from the right
 *   "right"           – slides in from the left
 */
export default function RevealClip({
  direction = "up",
  duration = 0.9,
  delay = 0,
  className = "",
  children,
  as: Tag = motion.div,
  ...rest
}) {
  const offsetMap = {
    up: { x: 0, y: 40 },
    down: { x: 0, y: -40 },
    left: { x: 40, y: 0 },
    right: { x: -40, y: 0 },
  };
  const offset = offsetMap[direction] || offsetMap.up;

  // Failsafe on slow / quirky mobile browsers: after 1.5s force the card
  // to be visible even if IntersectionObserver / animation didn't fire.
  const [forceShow, setForceShow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setForceShow(true), 1500);
    return () => clearTimeout(t);
  }, []);

  return (
    <Tag
      initial={{ opacity: 0, x: offset.x, y: offset.y }}
      whileInView={{
        opacity: 1,
        x: 0,
        y: 0,
        transition: { duration, ease: [0.22, 1, 0.36, 1], delay },
      }}
      animate={
        forceShow ? { opacity: 1, x: 0, y: 0 } : undefined
      }
      viewport={{ once: true, amount: 0.05 }}
      className={className}
      {...rest}
    >
      {children}
    </Tag>
  );
}
